import {h, Fragment} from "preact"
import { SerpElement, useCurrencyFormatting } from "@nosto/preact"
import { useEffect, useState } from "preact/hooks";
import { productImagePlaceholder, fetchShopifyProductObject, handleClickQuickAddButton } from "../helpers"

export default function Product({ product, index = 0 }) {
    const { format } = useCurrencyFormatting()
    const tags = [
        product.onSale
            ? {
                  label: "Sale",
                  class: "ns-badge ns-badge-green-outline ns-d-inline-block ns-text-align-center"
              }
            : undefined
    ].filter(v => !!v)

    const productName = product?.name?.split(" - ")[0];
    const productHandle = product?.url?.split("/")?.pop();
   
    const newURL = window.Shopify.routes?.root + 'products/' + productHandle;
    const discountAmount = product?.listPrice > product?.price ? parseInt((product?.listPrice - product?.price) / product?.listPrice * 100) : null;
    const isNew = product?.tags1?.includes("new");
    const isNewBadge = (() => {
        if (!product?.publishedAt) return false;
        const publishedDate = new Date(product.publishedAt);
        const daysSincePublish = (Date.now() - publishedDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSincePublish <= 90;
    })();
    
    const isBestSellerBadge = product?.scores?.purchase_7_days >= 5;
    const isTrendingBadge = product?.scores?.views >= 0.5;
    const isTopRatedBadge = product?.ratingValue >= 4.5 && product?.reviewCount > 10;
    const isAlmostGoneBadge = product?.inventoryLevel < 10;
    
    const dinkusBadge = product?.tags1?.find(tag => tag?.includes("dinkus__"))?.replace("dinkus__", "");
    
    // Extract badge text from product tags
    const badgeTag = product?.tags1?.find(tag => tag?.includes("badge:"));
    const badgeText = badgeTag ? badgeTag.replace("badge:", "").trim() : null;

    const [jsonObject, setJsonObject] = useState(null);
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    useEffect(()=>{
        async function fetchData() {
            const shopifyObject = await fetchShopifyProductObject(productHandle);
            setJsonObject(shopifyObject);
        }
        fetchData();
        console.log("product:", product);
        console.log("product.scores:", product?.scores);
        console.log("product.ratingValue:", product?.ratingValue);
        console.log("product.publishedAt:", product?.publishedAt);
    }, [])

   // Updated message generation with dynamic singular/plural handling
   const message = jsonObject?.options?.map(option => {
    const count = option?.values?.length || 0;
    const optionName = option?.name?.toLowerCase() || '';
    
        // Handle singular vs plural for display
        let displayName = optionName;
        if (count > 1) {
            // Convert singular forms to plural when count > 1
            if (optionName === 'size') {
                displayName = 'sizes';
            } else if (optionName === 'color') {
                displayName = 'colors';
            } else if (!optionName.endsWith('s')) {
                // Generic singular to plural (add 's' if it doesn't already end in 's')
                displayName = optionName + 's';
            }
        }
    
    return `${count} ${displayName}`;
    })?.join(" and ");

    const isOOS = product?.availability === "OutOfStock";
    const handle = product?.url?.split("/")?.pop();

    // 1. Split the productName variable by the ' - ' delimiter.
    const nameParts = product.name ? product?.name?.split(' - ') : [''];

    // 2. Assign the main name and subtitle. Handle cases where there is no subtitle.
    const mainName = nameParts[0];

    // Updated logic to match your Liquid template behavior
    // Check if we have enough parts and if the product has length- or waist- tags
    const hasLengthTag = product?.tags1?.some(tag => tag?.includes('length-'));
    const hasWaistTag = product?.tags1?.some(tag => tag?.includes('waist-'));

    let subtitle = null;
    if ((hasLengthTag || hasWaistTag) && nameParts.length > 3) {
        // Combine parts [1] and [2] like your Liquid template does
        subtitle = `${nameParts[1]} ${nameParts[2]}`.trim();
    } else if (nameParts.length > 1) {
        // Fallback for other cases - just use the first part after split
        subtitle = nameParts[1];
    }

    // Determine loading strategy based on position (first few images should not be lazy loaded for better LCP)
    const shouldLazyLoad = index >= 4; // First 4 products load eagerly
    const fetchPriority = index < 2 ? "high" : "low"; // First 2 products get high priority

    return (
        <SerpElement
            as="product-card"
            hit={product}
            ariaLabel={`Product ${product.name}`}
            class={`ns-product ns-markets product-card ns-border-box ns-clickable ns-text-undecorated ns-color-inherit ${isOOS ? "product-card__sold-out":""}`}
            data-handle={handle}
        >
            <div class={`product-card__actions ${isHovered ? "is--hovering" : ""}`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <a href={`${product?.url}`} tabindex="-1" class="product-card__link" data-product-link="" title={`Go to ${product?.name}`}>
                    <div class="media product-card__media product-card__media--hoverable" data-update-media="false">
                        <img
                            src={`${product.imageUrl}&width=720&format=webp`}
                            srcSet={`
                                ${product.imageUrl}&width=360 360w,
                                ${product.imageUrl}&width=480 480w,
                                ${product.imageUrl}&width=600 600w,
                                ${product.imageUrl}&width=720 720w,
                                ${product.imageUrl}&width=840 840w,
                                ${product.imageUrl}&width=960 960w,
                                ${product.imageUrl}&width=1280 1280w,
                                ${product.imageUrl}&width=1440 1440w,
                                ${product.imageUrl}&width=1800 1800w,
                                ${product.imageUrl}&width=2160 2160w,
                                ${product.imageUrl}&width=2880 2880w,
                                ${product.imageUrl} 3600w`}
                            sizes="(max-width: 360px) 360px,(max-width: 480px) 480px,(max-width: 600px) 600px,(max-width: 720px) 720px,(max-width: 840px) 840px,(max-width: 960px) 960px,(max-width: 1280px) 1280px,(max-width: 1440px) 1440px,(max-width: 1800px) 1800px,(max-width: 2160px) 2160px,(max-width: 2880px) 2880px, 100vw"
                            alt={product.name}
                            width="500"
                            height="750"
                            loading={shouldLazyLoad ? "lazy" : "eager"}
                            fetchpriority={fetchPriority}
                            decoding="async"
                        />

                        {product.alternateImageUrls?.length > 0 && (
                            <img
                                class="hover-img"
                                src={`${product.alternateImageUrls[0]}&width=720&format=webp`}
                                srcSet={`
                                    ${product.alternateImageUrls[0]}&width=360 360w,
                                    ${product.alternateImageUrls[0]}&width=480 480w,
                                    ${product.alternateImageUrls[0]}&width=600 600w,
                                    ${product.alternateImageUrls[0]}&width=720 720w,
                                    ${product.alternateImageUrls[0]}&width=840 840w,
                                    ${product.alternateImageUrls[0]}&width=960 960w,
                                    ${product.alternateImageUrls[0]}&width=1280 1280w,
                                    ${product.alternateImageUrls[0]}&width=1440 1440w,
                                    ${product.alternateImageUrls[0]}&width=1800 1800w,
                                    ${product.alternateImageUrls[0]}&width=2160 2160w,
                                    ${product.alternateImageUrls[0]}&width=2880 2880w,
                                    ${product.alternateImageUrls[0]} 3600w`}
                                sizes="(max-width: 360px) 360px,(max-width: 480px) 480px,(max-width: 600px) 600px,(max-width: 720px) 720px,(max-width: 840px) 840px,(max-width: 960px) 960px,(max-width: 1280px) 1280px,(max-width: 1440px) 1440px,(max-width: 1800px) 1800px,(max-width: 2160px) 2160px,(max-width: 2880px) 2880px, 100vw"
                                alt=""
                                aria-hidden="true"
                                width="500"
                                height="750"
                                loading={shouldLazyLoad ? "lazy" : "eager"}
                                fetchpriority="low"
                                decoding="async"
                            />
                        )}
                    </div>
                </a>
                {!isOOS && (
                    <>
                        <input type="hidden" name="id" value={product?.skus[0]?.id} />
                        <button type="button" class="quick-cart-drawer__trigger button product-card__add-to-cart--button color-scheme-6 button--icon" tabindex="0" aria-expanded="false" aria-controls="quick-cart-drawer" data-product-url={newURL} onClick={handleClickQuickAddButton}>
                            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 8V6C15 4.34315 13.6569 3 12 3C10.3431 3 9 4.34315 9 6V8M18 16V19M18 19V22M18 19H15M18 19H21M11 21H4.5L6.5 8H17.5L18.0973 11.8824" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                            </svg>
                            <span class="visually-hidden">Choose options</span>
                            <div class="quick-cart-drawer__trigger--loading">
                                <div class="lds-ring">
                                <span></span>
                                <span></span>
                                <span></span>
                                <span></span>
                                </div>
                            </div>
                        </button>
                    </>
                )}
                <div class="collection-product__tags">
                    {isNew && <span class="collection-product__tag">New</span>}
                </div>
            </div>
            <div class="product-card__content">
                <div class="product-card__info product-card__badge--on-start">
                    <h3 class="product-card__title">
                        <a href={`${product?.url}`} class="product-card__link" tabindex="0" data-product-link="" title={`Go to ${product?.name}`}>
                        <div className="ns-product-title">
                            <div className="ns-product-name">
                                {mainName}
                            </div>
                            {subtitle && (
                                <span className="ns-product-subtitle" style="font-weight: 500;">
                                    {subtitle}
                                </span>
                            )}
                            {badgeText && (
                                <div className="ns-product-badge" style="margin-top: 4px; font-size: 1rem; color: #E4221F;">
                                    {badgeText}
                                </div>
                            )}
                        </div>
                        </a>
                        <span class="product-card__badges">
                        {(() => {
                            if (isNewBadge) {
                                return <span class="ns-badge" style="background-color: #FFF; color: #000;">New</span>;
                            } else if (isBestSellerBadge) {
                                return <span class="ns-badge" style="background-color: #E4221F; color: #fff;">Best Seller</span>;
                            } else if (isTrendingBadge) {
                                return <span class="ns-badge" style="background-color: #FFF; color: #000;">Trending</span>;
                            } else if (isAlmostGoneBadge) {
                                return <span class="ns-badge" style="background-color: #E4221F; color: #fff;">Almost Gone</span>;
                            } else if (dinkusBadge) {
                                return (
                                <span
                                    class="ns-badge"
                                    style={
                                    dinkusBadge === 'Sale'
                                        ? "background-color: #E4221F; color:#fff;"
                                        : "background-color: #FFF; color:#000;"
                                    }
                                >
                                    {dinkusBadge}
                                </span>
                                );
                            } else if (discountAmount) {
                                return (
                                <span class="ns-badge" style="background-color: #E4221F; color:#fff;">
                                    <span class="percentage">{discountAmount}</span>% off
                                </span>
                                );
                            } else if (isTopRatedBadge) {
                                return <span class="ns-badge" style="background-color: #FFF; color: #000;">Top Rated</span>;
                            } else if (product?.availability === "OutOfStock") {
                                return (
                                <span class="product-card__badge product-card__badge--rounded">Sold out</span>
                                );
                            }
                            return null;
                            })()}
                        </span>
                        
                    </h3>
                    <span class="product-card__price">
                        <div class={`price ${product?.listPrice > product?.price ? "price--on-sale" : ""}`}>
                        <div class="price__container" data-label-price-regular="Regular price" data-label-price-sale="Sale price" data-label-price-from="From" data-label-price-min="Minimum price" data-money-format="${{amount}}">
                            {product?.listPrice > product?.price ? (
                                <div class="price__sale">
                                    <div class="price__sale-inner">
                                        <span class="visually-hidden">Regular price</span>
                                        <s style="margin-right:3px;" class="ns-list-price">{format(product?.listPrice)}</s>
                                        <ins><span class="visually-hidden">Minimum price</span>
                                        <span class="ns-price">{format(product?.price)}</span></ins>
                                    </div>
                                </div>
                            ):(
                                <div class="price__regular">
                                    <span class="visually-hidden">Regular price</span>
                                    <span class="ns-price">{format(product?.price)}</span>
                                </div>
                            )}
                        </div>
                        </div>
                    </span>
                </div>
                <div class="product-card__options product-card__options--normal">
                    <div class="product__product-groups"></div>
                    <div class="product-card__variants">
                        {message && <span class="product-card__variants--info ">Available in {message}</span>}
                        <ul class="product-card__variants--list product-card__variants--list--normal">
                        <li class="hidden">
                            <fieldset class="product__variant-options js-product-card-options" role="radiogroup">
                                {product?.skus?.slice(0,3)?.map((sku, skuIndex)=>{
                                    const size = sku?.customFields?.find(field => field?.key === 'Size')?.value;
                                    return (
                                        <div class="button--variant" tabindex="0">
                                            <input id={`template--24643769008500__main-${product?.productId}-variant-option-1-${skuIndex}-block`} type="radio" name={`${product?.productId}-option[1]`} class="product-option__input hidden variant-option-radio-input variant-option-radio-input visually-hidden" value={size} aria-label={size} data-variant-quantity={sku?.inventoryLevel} checked={skuIndex === 0} />
                                            <label for={`template--24643769008500__main-${product?.productId}-variant-option-1-${skuIndex}-block`} title={size} class="product-option__label product-option__label--rounded product-option__label--circle">
                                            <span class="">{size}</span>
                                            </label>
                                        </div>
                                    )
                                })}
                                {product?.skus?.length > 3 && <li class="product-card__variants--more">+{product?.skus?.length - 3}</li>}
                            </fieldset>
                        </li>
                        </ul>
                    </div>
                </div>
            </div>
        </SerpElement>
    )
}